import React, { useState, useContext } from "react";
import { MdEdit } from "react-icons/md";
import { Trash2 } from "lucide-react";
import { ChartNoAxesGantt } from "lucide-react";
import {
  TASK_MASTER_COLUMNS,
  TASK_MASTER_LABELS,
  MAIN_TASKS_COLUMN_WIDTHS,
} from "./constants";
import { PROJECT_TYPE, CATEGORY_TYPE } from "../constants";
import { UserContext } from "../../../Context";
import { ROLES } from "../../../Constants/Strings";

export default function TaskMasterTable({
  taskMasters,
  onView,
  onEdit,
  onDelete,
  onDrillDown,
  isSubTaskView = false, // viewing main task list by default
}) {
  const { user } = useContext(UserContext);
  const userRole = user?.role;

  const restrictedRoles = [ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER];

  const renderFields = (key, row) => {
    switch (key) {
      case "actions":
        return (
          <div
            className="flex justify-center gap-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Show button only on main task list */}
            {!isSubTaskView && (
              <button
                className="bg-white text-primary p-1 rounded-md max-h-fit cursor-pointer border-[1px] border-blue-500"
                onClick={() => onDrillDown(row)}
                title="View Subtasks"
              >
                <ChartNoAxesGantt size={15} />
              </button>
            )}

            <MdEdit
              size={20}
              className={`text-primary ${
                restrictedRoles.includes(userRole)
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              onClick={
                restrictedRoles.includes(userRole)
                  ? undefined
                  : () => onEdit(row)
              }
            />

            <Trash2
              size={20}
              className={`text-red-500 ${
                restrictedRoles.includes(userRole)
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              onClick={
                restrictedRoles.includes(userRole)
                  ? undefined
                  : () => onDelete(row)
              }
            />
          </div>
        );
      case "sub_tasks":
        return row.sub_tasks?.length || 0;
      case "project_type":
        return PROJECT_TYPE[row.project_type];
      case "project_category":
        return CATEGORY_TYPE[row.project_category];
      default:
        return row[key];
    }
  };

  return (
    <div className="overflow-y-auto md:h-[52vh] h-[46vh]">
      {/* Desktop view */}
      <div className="hidden md:block">
        <table className=" w-full text-left text-gray-500 dark:text-gray-400">
          {/* Head */}
          <thead className="text-sm text-gray-700 bg-gray-50 dark:bg-gray-700 dark:text-gray-400 top-0 sticky z-10">
            <tr>
              {(isSubTaskView
                ? TASK_MASTER_COLUMNS.filter((col) => col !== "sub_tasks")
                : TASK_MASTER_COLUMNS
              ).map((col) => (
                <th
                  key={col}
                  className={`px-4 py-3 ${MAIN_TASKS_COLUMN_WIDTHS[col]} text-center`}
                >
                  {TASK_MASTER_LABELS[col]}
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {taskMasters?.length === 0 ? (
              <tr>
                <td
                  colSpan={TASK_MASTER_COLUMNS.length}
                  className="px-4 py-4 text-center text-gray-500"
                >
                  No sub-tasks available
                </td>
              </tr>
            ) : (
              taskMasters?.map((task, index) => (
                <tr
                  key={task.id}
                  onClick={() => onView(task)}
                  className={`border-b cursor-pointer ${
                    index % 2 === 0
                      ? "bg-white dark:bg-gray-900"
                      : "bg-gray-50 dark:bg-gray-800"
                  }`}
                >
                  {(isSubTaskView
                    ? TASK_MASTER_COLUMNS.filter((col) => col !== "sub_tasks")
                    : TASK_MASTER_COLUMNS
                  ).map((col) => (
                    <td
                      key={col}
                      className={`px-4 py-4 text-sm text-center ${
                        MAIN_TASKS_COLUMN_WIDTHS[col]
                      } ${
                        col === "title"
                          ? "font-medium text-gray-900 dark:text-white truncate max-w-full"
                          : ""
                      }`}
                    >
                      {renderFields(col, task)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile view */}
      <div className="md:hidden flex flex-col gap-3">
        {taskMasters?.length === 0 ? (
          <div className="text-center text-gray-500 py-4">
            No sub-tasks available
          </div>
        ) : (
          taskMasters?.map((task, index) => (
            <div
              key={task.id}
              onClick={() => onView(task)}
              className="border-b-2 p-3 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition cursor-pointer"
            >
              <div className="flex justify-between">
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-base mb-1">
                    {task.title}
                  </div>

                  <div className="flex justify-start gap-2 text-xs text-gray-600 dark:text-gray-300 mb-1">
                    <span>Type: {PROJECT_TYPE[task.project_type] || "—"}</span>
                    <span>|</span>
                    <span>
                      Category: {CATEGORY_TYPE[task.project_category] || "—"}
                    </span>
                  </div>

                  <div className="flex justify-start gap-2 text-xs text-gray-600 dark:text-gray-300 mb-2">
                    <span>Weight: {task.weight || "—"}</span>
                    {!isSubTaskView && (
                      <>
                        <span>|</span>
                        <span>Sub tasks: {task.sub_tasks?.length || 0}</span>
                      </>
                    )}
                  </div>
                </div>

                <div
                  className="flex justify-end gap-4 pt-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {!isSubTaskView && (
                    <button
                      className="bg-white text-primary p-1 rounded-md max-h-fit cursor-pointer border-[1px] border-blue-500"
                      onClick={() => onDrillDown(task)}
                      title="View Subtasks"
                      
                    >
                      <ChartNoAxesGantt size={15} />
                    </button>
                  )}
                  <MdEdit
                    size={18}
                    className={`text-primary ${
                      restrictedRoles.includes(userRole)
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    onClick={
                      restrictedRoles.includes(userRole)
                        ? undefined
                        : () => onEdit(task)
                    }
                  />
                  <Trash2
                    size={18}
                    className={`text-red-500 ${
                      restrictedRoles.includes(userRole)
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    onClick={
                      restrictedRoles.includes(userRole)
                        ? undefined
                        : () => onDelete(task)
                    }
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
